import { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useWatch } from "react-hook-form";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { structure as structureApi } from "@/endpoints/dryp/structure";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { useServerTable } from "@/hooks/useServerTable";
import { useTableFilters } from "@/hooks/useTableFilters";
import { getTableStructureFilterParams } from "@/utils/filters";
import { List } from "@/components/List";
import { SearchInput } from "@/components/SearchInput";
import { StructureFilters } from "@/components/filters/StructureFilters";
import { SelectColumn } from "@/components/tableColumns/SelectColumn";
import { StructureTypeIcon } from "@/components/StructureTypeIcon";
import { GroupBadgeList } from "@/components/badges/GroupBadge";
import { CUSTOM_FILTERS } from "@/config/list";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { FieldGroup } from "./components/FieldGroup";
import { useAlarmWizardStore } from "./useAlarmWizardStore";

// TODO: wizard does not yet capture a real Dryp.ParameterId for the alarm; the structures
// endpoint requires parameterIds so we send this default until that mapping is wired up.
const DEFAULT_PARAMETER_ID = 189 as Dryp.ParameterId;

const SCOPE_OPTIONS: { value: Dryp.AlarmScope; label: string; description: string }[] = [
	{
		value: "all",
		label: "All structures",
		description: "Applies to every structure where this condition is relevant.",
	},
	{
		value: "include",
		label: "Specific structures",
		description: "Pick the exact structures this alarm should monitor.",
	},
];

const structureColumns: ColumnDef<Dryp.Structure>[] = [
	SelectColumn<Dryp.Structure>({ id: "id", mode: "multi" }),
	{
		accessorKey: "name",
		header: "Name",
		cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
	},
	{
		accessorKey: "type.name",
		id: "type.name",
		enableSorting: false,
		header: "Type",
		cell: ({ row }) => (
			<div className="inline-flex">
				<StructureTypeIcon typeId={row.original.type.id} showLabel />
			</div>
		),
	},
	{
		accessorKey: "group",
		id: "group",
		enableSorting: false,
		header: "Groups",
		cell: ({ row }) => {
			const groups = row.original.groups ?? [];
			return <GroupBadgeList groups={groups} selectedIds={new Set(groups.map(({ id }) => id))} maxVisible={2} />;
		},
	},
];

const StructureTable = observer(
	({ value, onChange }: { value: Dryp.StructureId[]; onChange: (ids: Dryp.StructureId[]) => void }) => {
		const structuresApi = useAsyncStateStore(structureApi.getAllStructures, { debounce: 300 });

		const { table, tableState } = useServerTable({
			columns: structureColumns,
			data: structuresApi.data?.items || [],
			fetchFn: params => {
				const structureFilters = getTableStructureFilterParams(params?.filters);
				return structuresApi.fetch(
					{ pageNumber: params.page, pageSize: params.pageSize },
					{ ...structureFilters, parameterIds: [DEFAULT_PARAMETER_ID] },
					params.sort,
				);
			},
			initialValues: {
				page: 0,
				pageSize: 5,
			},
		});

		useEffect(() => {
			if (value.length === 0) return;
			const hydrated: RowSelectionState = Object.fromEntries(value.map(structureId => [structureId, true]));
			tableState.setRowSelection(hydrated);
			// Hydrate once on mount — subsequent changes flow from the table into the form.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const selectedIds = useMemo(
			() =>
				Object.keys(tableState.rowSelectionSnapshot).filter(
					id => tableState.rowSelectionSnapshot[id],
				) as Dryp.StructureId[],
			[tableState.rowSelectionSnapshot],
		);

		// Hydration race: on (re)mount the hydration effect schedules a
		// setRowSelection update, but this effect's first run still sees
		// selectedIds as []. Without the ref guard we'd fire onChange([])
		// and wipe the saved structureIds when the user navigates back to
		// this step. The guard releases as soon as the table's selection
		// settles, so legitimate "deselect everything" still propagates.
		const hydratedRef = useRef(false);
		useEffect(() => {
			if (!hydratedRef.current && value.length > 0 && selectedIds.length === 0) return;
			hydratedRef.current = true;
			if (value.length === selectedIds.length && value.every(id => selectedIds.includes(id))) return;
			onChange(selectedIds);
		}, [selectedIds, value, onChange]);

		const searchValue = (tableState.columnFilters.find(f => f.id === CUSTOM_FILTERS.search)?.value ?? "") as string;

		const listFilterApi = useTableFilters(table);

		return (
			<div className="space-y-3">
				<div className="flex flex-wrap items-end gap-2">
					<SearchInput
						value={searchValue}
						onChange={searchTerm => listFilterApi.setFilter(CUSTOM_FILTERS.search, searchTerm)}
					/>
					<StructureFilters filter={listFilterApi} />
				</div>

				<List
					table={table}
					layout={{ loading: structuresApi.loading }}
					pagination={{ total: structuresApi.data?.query?.pagination?.totalItems || 0 }}
					toolbar={{ hideToolbar: true }}
				/>
			</div>
		);
	},
);

export const StepScope = observer(() => {
	const state = useAlarmWizardStore();
	const structureScope = useWatch({ control: state.form.control, name: "structureScope" });

	return (
		<div className="space-y-8">
			<div>
				<h3 className="text-base font-semibold">Where this alarm runs</h3>
				<p className="text-sm text-muted-foreground mt-0.5">Choose which structures this alarm monitors.</p>
			</div>

			<FormField
				control={state.form.control}
				name="structureScope"
				render={({ field }) => (
					<FormItem>
						<FieldGroup label="Structures" hint="Apply to all structures, or pick a specific list.">
							<FormControl>
								<RadioGroup
									value={field.value}
									onValueChange={value => {
										const next = value as Dryp.AlarmScope;
										field.onChange(next);
										if (next === "all") {
											state.form.setValue("structureIds", [], { shouldValidate: false });
										}
									}}
									className="space-y-2"
								>
									{SCOPE_OPTIONS.map(option => {
										const isActive = field.value === option.value;
										return (
											<label
												key={option.value}
												className={cn(
													"flex items-center gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors",
													isActive
														? "border-primary bg-primary/5"
														: "border-border hover:bg-accent",
												)}
											>
												<RadioGroupItem value={option.value} />
												<div>
													<span className="text-sm font-medium">{option.label}</span>
													<p className="text-xs text-muted-foreground mt-0.5">
														{option.description}
													</p>
												</div>
											</label>
										);
									})}
								</RadioGroup>
							</FormControl>
						</FieldGroup>
						<FormMessage />
					</FormItem>
				)}
			/>

			{structureScope === "include" && (
				<FormField
					control={state.form.control}
					name="structureIds"
					render={({ field }) => (
						<FormItem>
							<StructureTable value={field.value} onChange={field.onChange} />
						</FormItem>
					)}
				/>
			)}
		</div>
	);
});
